import React, { useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import PropertyGeneralDataCard from './PropertyGeneralDataCard';
import { initialState, reducer } from '../reducers/generalData/containerReducer';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { propertyValidator } from '../../validators';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { PROPERTY_TYPES_QUERY, PROPERTY_CATEGORIES_QUERY, CONTEXTS_QUERY, APPLICATIONS_QUERY } from '../../queries';
import { SAVE_PROPERTY } from '../../mutations';
import { PROPERTY_SAVED } from '../../subscriptions';

const PropertyGeneralDataContainer = ({ data, onChangePropertyTitle, onRedirect }) => {
  const [localState, dispatch] = useReducer(reducer, initialState);
  const [propertyValidation, validateProperty] = useDirtyFieldValidation(propertyValidator);
  const addToast = useToast();
  const { t } = useTranslation();

  const { data: propertyTypesData, refetch: refetchPropertyTypes } = useQuery(PROPERTY_TYPES_QUERY);

  const { data: propertyCategoriesData, refetch: refetchPropertyCategories } = useQuery(PROPERTY_CATEGORIES_QUERY);

  const { data: contextsData, refetch: refetchContexts } = useQuery(CONTEXTS_QUERY);

  const { data: applicationsData } = useQuery(APPLICATIONS_QUERY);

  const [saveProperty] = useMutation(SAVE_PROPERTY, {
    onError: () => {
      dispatch({ type: 'OnPropertySaveFailed' });
    }
  });

  useSubscription(PROPERTY_SAVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('Property.PropertySaved'), 'success');
      const propertyId = data.propertySaved.id;
      const propertyContexts = data.propertySaved.propertyContexts;
      dispatch({ type: 'OnPropertySaved', id: propertyId, propertyContexts: propertyContexts });
      if (!localState.property.id) onRedirect(propertyId);

      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedProperties' });
      client.cache.evict({ id: `ROOT_QUERY`, fieldName: `property({"propertyId":${data.propertySaved.id}})` });
      client.cache.gc();
    }
  });

  const handleLookupPropertyChanged = event => {
    dispatch({
      type: 'OnPropertyContextChange',
      event
    });
  };

  const handleApplicationsChanged = event => {
    dispatch({
      type: 'OnPropertyApplicationsChange',
      event
    });
  };

  const handleSave = useCallback(() => {
    // eslint-disable-next-line no-unused-vars
    const { changed, categoryName, propertyTypeName, propertyTypeCode, saving, ...property } = localState.property;
    const input = {
      ...property,
      propertyApplications: property.propertyApplications.map(x => ({
        appCode: x.appCode
      }))
    };
    if (!validateProperty(input)) return;
    dispatch({ type: 'OnPropertySaveStarted' });
    return saveProperty({ variables: { input } });
  }, [saveProperty, localState.property, validateProperty]);

  const handleChange = (propertyName, value) => {
    dispatch({ type: 'OnPropertyValuesChanged', propertyName, value });
  };

  const onLanguageChange = useCallback(() => {
    refetchPropertyCategories();
    refetchPropertyTypes();
    refetchContexts();
  }, [refetchPropertyCategories, refetchPropertyTypes, refetchContexts]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
  }, [data]);

  useEffect(() => {
    validateProperty(localState.property);
  }, [localState.property, validateProperty]);

  useEffect(() => {
    onChangePropertyTitle(localState.property.propertyName);
  }, [localState.property.propertyName, onChangePropertyTitle]);

  return (
    <PropertyGeneralDataCard
      property={localState.property}
      propertyTypes={propertyTypesData ? propertyTypesData.propertyTypes : []}
      propertyCategories={propertyCategoriesData ? propertyCategoriesData.propertyCategories : []}
      contexts={contextsData ? contextsData.contexts : []}
      applications={applicationsData ? applicationsData.applicationsConfig : []}
      onChange={handleChange}
      onSave={handleSave}
      onLookupPropertyChanged={handleLookupPropertyChanged}
      onApplicationsChanged={handleApplicationsChanged}
      validation={propertyValidation}
    />
  );
};

PropertyGeneralDataContainer.propTypes = {
  data: PropTypes.object,
  onChangePropertyTitle: PropTypes.func,
  onRedirect: PropTypes.func,
  propertyId: PropTypes.number
};

export default PropertyGeneralDataContainer;
